import React, { useContext, useEffect, useRef, useState } from "react";

import { Button, Space, Typography } from "antd";
import { CompassOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
	K_INFOWINDOW_MARGIN_SIDES,
	K_INFOWINDOW_WIDTH,
	ListingInfoWindow,
} from "../../../../Components/Map/Listing/InfoWindow/web";
import {
	K_LISTING_MARKER_SIZE,
	ListingMarker,
} from "../../../../Components/Map/Listing/Marker/web";

import GoogleMapReact from "google-map-react";
import { RTBhouse } from "../../../../Components/RTBhouse/web";
import { SearchResultDisplay } from "../SearchResultsCards/SearchResultsPagination/ResultDisplay/web";
import getConfig from "next/config";
import { DEFAULTS_CENTERS, useSearchResultsMapWithBackend } from "./SearchResultsMap.hook";
import { useTheme } from "../../../../Styles/ThemeHook";
import { CloseButton } from "shared-components/Components/CustomIcons/CloseButton/web";
import { useFilters } from "shared-components/Components/Filters/Filters.hook";
import { fetchSearchResults } from "src/services/search";
import { filterMarkers } from "@Utils/Functions";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import MarkerClusterer from "@google/markerclustererplus";
import { Listing } from "shared-components";
import "./styles.less";

const { googleMapsApiKey } = getConfig().publicRuntimeConfig;

const { Text } = Typography;

/**
 * @todo    multiple markers, same latlng
 * @todo    click on marker q es proyecto, rompe todo
 */

const DEFAULT_ZOOM_LEVEL = 14;
const MAX_POINTS_TO_SHOW = 100;
export const MIN_ZOOM_TO_SHOW_PRICE_MARKER = 12;

const SearchResultsMap = () => {
	const MAP = useRef(null);
	const MAPS = useRef(null);
	const { theme } = useTheme();
	const { country_code } = useContext(ConfigStateContext);
	const { filters } = useFilters();
	const {
		polygon,
		setPolygon,
		selectMarker,
		selectedMarker,
		mapRegionChanged,
		removeLocation,
	} = useSearchResultsMapWithBackend();
	const [activeMarkers, setActiveMarkers] = useState<Listing[]>([]);
	const [totalMarkers, setTotalMarkers] = useState(0);
	const [loadingMarkers, setLoadingMarkers] = useState(false);
	const [isMapFeatured, setIsMapFeatured] = useState<Listing[]>([]);
	const [paginatorInfo, setPaginatorInfo] = useState<Listing[]>([]);
	const [errorOnLoad, setErrorOnLoad] = useState(false);
	const [mapCenter, setMapCenter] = useState(DEFAULTS_CENTERS[country_code.toUpperCase()]);
	const [editing, setEditing] = useState(false);
	const [listing, setListing] = useState<Listing>();
	const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);
	const [visibleMarkers, setVisibleMarkers] = useState<Listing[]>([]);
	const [zoomPrecio, setZoomPrecio] = useState(false);
	const [infoWindowProps, setInfoWindowProps] = useState({ position: "left" });
	const [mapMoved, setMapMoved] = useState<any>();
	const [actionSearchOrigin, setActionSearchOrigin] = useState("general");

	const eventMap = "ontouchstart" in document.documentElement ? "touchstart" : "mousedown";

	const markerCluster = useRef(null);
	const markersRef = useRef([]);
	const zoomThreshold = useRef(16);

	const transformCoordinates = (coords) => {
		if (!Array.isArray(coords) || coords.length !== 2) {
			throw new Error('Invalid coordinates format');
		}

		return {
			lat: coords[1],
			lng: coords[0],
		};
	};

	useEffect(() => {
		const filterMarkersByZoom = () => {
			let maxMarkersToShow;
			switch (true) {
				case zoom <= 13:
					maxMarkersToShow = 5;
					break;
				case zoom >= 14 && zoom <= 15:
					maxMarkersToShow = 15;
					break;
				default:
					maxMarkersToShow = activeMarkers.length;
			}

			const filteredMarkers = activeMarkers.slice(0, maxMarkersToShow);
			setVisibleMarkers(filteredMarkers);
		};

		filterMarkersByZoom();
	}, [zoom, activeMarkers]);


	useEffect(() => {
		if (filters?.locations?.[0]?.location_point?.coordinates) {
			setMapCenter(transformCoordinates(filters.locations[0].location_point.coordinates));
		} else {
			setMapCenter(DEFAULTS_CENTERS[country_code.toUpperCase()]);
		}
	}, [filters?.locations?.[0]?.location_point?.coordinates, country_code]);

	useEffect(() => {
		if (mapCenter) {
			getSearchResults();

			if (MAPS.current && MAPS.current.drawingPolygon) {
				MAPS.current.drawingPolygon.setMap(null);
				MAPS.current.drawingPolygon = null;
			}
			setEditing(false);
			setPolygon(null);
			setZoom(DEFAULT_ZOOM_LEVEL)
		}
	}, [mapCenter]);

	useEffect(() => {
		if (!mapCenter || mapCenter == "undefined")
			return

		getSearchResults();
	}, [mapCenter]);

	useEffect(() => {
		if (filters && filters.map_polygon) getSearchResults();
	}, [filters.map_polygon])

	const getSearchResults = () => {
		setLoadingMarkers(true)

		const payload = {
			rows: MAX_POINTS_TO_SHOW,
			params: { ...filters, allowMapInfo: true },
			page: filters?.page || 1,
			source: 10,
		};

		fetchSearchResults(payload)
			.then((res: any) => {
				if (res) {
					setLoadingMarkers(false)
					const raw =
						filters && filters.map_polygon
							? res?.searchFast?.data
							: filterMarkers(res?.searchFast?.data);

					const raw2 = raw.filter((item: any) => !item.isMapFeatured);

					const featuredMarkers = raw.filter((item: any) => item.isMapFeatured);

					setIsMapFeatured(featuredMarkers);
					setActiveMarkers(raw2);
					setTotalMarkers(raw.length);
					setPaginatorInfo(res.searchFast.paginatorInfo)
				}
			})
			.catch((error) => {
				console.warn("ERROR ", error)
				setErrorOnLoad(true)
			})
	}

	const handleApiLoaded = (__map, __maps) => {
		MAP.current = __map;
		MAPS.current = __maps;
		MAPS.current.editing = editing;
		MAPS.current.drawing = false;
		MAPS.current.drawingPolygon = null;
		MAPS.current.savedPolygon = null;

		markerCluster.current = new MarkerClusterer(__map, [], {
			imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
		});

		const updateClusters = () => {
			if (MAP.current.getZoom() >= zoomThreshold.current) {
				markerCluster.current.clearMarkers();
			} else {
				markerCluster.current.addMarkers(markersRef.current);
			}
		};

		MAP.current.addListener('zoom_changed', updateClusters);

		MAPS.current.event.addDomListener(MAP.current.getDiv(), eventMap, function (e) {
			if (MAPS.current.editing) {
				if (MAPS.current.drawingPolygon) {
					MAPS.current.drawingPolygon.setMap(null);
				}
				MAPS.current.drawingPolygon = new MAPS.current.Polyline({
					map: MAP.current,
					clickable: false,
				});
				MAPS.current.drawing = true;
			}
		});

		MAPS.current.event.addListener(MAP.current, "mousemove", function (e) {
			if (MAPS.current.drawing) {
				MAPS.current.drawingPolygon.getPath().push(e.latLng);
			}
		});

		MAPS.current.event.addListener(MAP.current, "mouseup", function (e) {
			if (MAPS.current.drawing) {
				MAPS.current.drawing = false;
				setEditing(false);
				setActionSearchOrigin("draw");
				setPolygon(
					MAPS.current.drawingPolygon
						.getPath()
						.getArray()
						.map(o => {
							return {
								latitude: o.lat(),
								longitude: o.lng(),
							};
						})
				);
				MAPS.current.drawingPolygon.setMap(null);
			}
		});

		updatePolygon();
		updateMapOptions();
		updateClusters();
	};

	const updateMapOptions = () => {
		if (!MAP.current && !MAPS.current) return;
		MAP.current.setOptions({
			clickableIcons: false,
			draggable: !editing,
			draggableCursor: editing ? "crosshair" : "grab",
			zoomControl: !editing,
			scrollwheel: !editing,
			disableDoubleClickZoom: editing,
		});
	};

	const updatePolygon = () => {
		const showDraw = actionSearchOrigin === "draw";
		if (!MAP.current && !MAPS.current) return;
		if (MAPS.current.savedPolygon !== null) {
			MAPS.current.savedPolygon.setMap(null);
		}
		if (polygon.length === 0) return;

		MAPS.current.savedPolygon = new MAPS.current.Polygon({
			paths: polygon.map(o => {
				return { lat: o.latitude, lng: o.longitude };
			}),
			strokeColor: theme.colors.primaryColor,
			strokeOpacity: showDraw ? 1 : 0,
			strokeWeight: 2,
			fillColor: theme.colors.primaryColor,
			fillOpacity: showDraw ? 0.4 : 0,
			clickable: false,
			map: MAP.current,
		});

		let bounds = new MAPS.current.LatLngBounds();
		MAPS.current.savedPolygon.getPath().forEach(function (latLng) {
			bounds.extend(latLng);
		});

		// MAP.current.fitBounds(bounds);
	};

	const enableGPS = () => {
		navigator.geolocation.getCurrentPosition(
			x => {
				setEditing(false);
				MAP.current.setZoom(MIN_ZOOM_TO_SHOW_PRICE_MARKER);
				MAP.current.setCenter({
					lat: x.coords.latitude,
					lng: x.coords.longitude,
				});
			},
			x => {
				//@todo
				console.error(x);
			}
		);
	};

	const updateInfoWindowPosition = markerBoundaries => {
		const safe_width = K_INFOWINDOW_WIDTH + K_INFOWINDOW_MARGIN_SIDES * 2;
		const mapBoundaries = MAP.current.getDiv().getBoundingClientRect();
		const { x1, x2 } = {
			x1: markerBoundaries.left - mapBoundaries.left,
			x2: mapBoundaries.right - markerBoundaries.right,
		};

		if (x1 > safe_width) {
			setInfoWindowProps({ position: "left" });
		} else {
			setInfoWindowProps({ position: "right" });
		}
	};

	const handleSearchZone = (x) => {
		selectMarker(0);
		removeLocation();
		setZoom(x.zoom);
		setMapCenter(x.center);
		setActionSearchOrigin("general")

		const bounds = [
			{ latitude: x.bounds.ne.lat, longitude: x.bounds.ne.lng },
			{ latitude: x.bounds.nw.lat, longitude: x.bounds.nw.lng },
			{ latitude: x.bounds.sw.lat, longitude: x.bounds.sw.lng },
			{ latitude: x.bounds.se.lat, longitude: x.bounds.se.lng },
			{ latitude: x.bounds.ne.lat, longitude: x.bounds.ne.lng },
		];
		const zoomLevel = x.zoom;

		if (!zoomPrecio && zoomLevel >= MIN_ZOOM_TO_SHOW_PRICE_MARKER) {
			setZoomPrecio(true);
		} else if (zoomPrecio && zoomLevel < MIN_ZOOM_TO_SHOW_PRICE_MARKER) {
			setZoomPrecio(false);
		}

		mapRegionChanged(bounds, zoomLevel);
	}

	const startDrawing = () => {
		setEditing(true);
		setPolygon(null);
	};

	const deleteDrawing = () => {
		setEditing(false);
		setPolygon(null);
	};

	const handleClick = x => selectMarker(0);

	const handleChildClick = (x, y) => {
		selectMarker(x.id);
		setListing(x);
	};

	const parseLat = (lat: number | string): number => {
		if (typeof lat !== "number") {
			return parseFloat(parseFloat(lat).toFixed(7))
		}
		return parseFloat(lat.toFixed(7))
	}

	useEffect(() => updatePolygon(), [polygon]);

	useEffect(() => {
		if (MAPS.current) MAPS.current.editing = editing;
		updateMapOptions();
	}, [editing]);

	useEffect(() => {
		if (!MAP.current && !MAPS.current) return;

		if (activeMarkers.length > 0) {
			let bounds = new MAPS.current.LatLngBounds();
			activeMarkers.forEach(e => bounds.extend({ lat: parseLat(e.latitude), lng: parseLat(e.longitude) }));
		}
	}, [activeMarkers]);

	useEffect(() => {
		if (markerCluster.current && activeMarkers.length) {
			markersRef.current = activeMarkers.map(m => new MAPS.current.Marker({
				position: { lat: parseLat(m.latitude), lng: parseLat(m.longitude) },
				icon: "/icons/marker.png",
			}));

			markerCluster.current.clearMarkers();

			if (MAP.current.getZoom() >= zoomThreshold.current) {
				markerCluster.current.clearMarkers();
			} else {
				markerCluster.current.addMarkers(markersRef.current);
			}
		}
	}, [activeMarkers, parseLat]);

	if (!mapCenter) return <></>;

	return (
		<div className="map-container">
			{/* Card Default */}

			{!!listing && (
				<ListingInfoWindow
					onClose={handleClick}
					show={selectedMarker > 0}
					listingId={selectedMarker}
					property={listing}
					position={infoWindowProps.position}
				/>
			)}

			{/* SearchResultDisplay */}
			<div className="map-search-result">
				<Space direction="vertical" size={0}>
					<Text strong>
						<SearchResultDisplay
							activeMarkers={activeMarkers.length}
							totalMarkers={totalMarkers}
							loading={loadingMarkers}
							error={errorOnLoad}
							paginatorInfo={paginatorInfo}
						/>
					</Text>
					<Text style={{ fontSize: 12 }}>
						{loadingMarkers
							? "Buscando propiedades"
							: "Mueve el mapa o haz zoom para buscar más"}
					</Text>
				</Space>
			</div>

			{!loadingMarkers && (
				<div className={"map-top-buttons"}>
					{filters && filters.map_polygon && actionSearchOrigin === "draw" ?
						<button
							className="btn-secondary"
							onClick={() => deleteDrawing()}
						>
							<DeleteOutlined />
							Borrar
						</button> :
						editing ?
							<button
								className="btn-secondary"
								onClick={() => deleteDrawing()}
							>
								<CloseButton />
								Terminar
							</button> :
							<button
								className="btn-secondary"
								onClick={() => startDrawing()}
							>
								<EditOutlined />
								Dibujar
							</button>
					}

					<button
						className="btn-secondary"
						onClick={() => handleSearchZone(mapMoved)}
					>
						Buscar en esta área
					</button>
				</div>
			)}

			{/* ButtonsActionsMap */}
			<div className={"map-search-buttons"}>
				<Space direction="vertical" size={theme.spacing.smSpacing}>
					<Button onClick={() => enableGPS()} className={"secondary"}>
						<Space direction="vertical" size={-2}>
							<CompassOutlined />
							{"GPS"}
						</Space>
					</Button>
				</Space>
			</div>

			{/* Map */}
			<GoogleMapReact
				bootstrapURLKeys={{ key: googleMapsApiKey }}
				center={mapCenter}
				options={{
					fullscreenControl: false,
					minZoom: 10,
					maxZoom: 18,
				}}
				defaultZoom={DEFAULT_ZOOM_LEVEL}
				onChange={x => {
					setMapMoved(x)
					setZoom(x.zoom);
					// handleSearchZone(x);
				}}
				onClick={handleClick}
				distanceToMouse={(markerPos, mousePos, markerProps) => {
					const x = markerPos.x + K_LISTING_MARKER_SIZE / 2;
					const y = markerPos.y + K_LISTING_MARKER_SIZE / 2;

					// and i want that hover probability on markers with text === 'A' be greater than others
					// so i tweak distance function (for example it's more likely to me that user click on 'A' marker)
					// another way is to decrease distance for 'A' marker
					// this is really visible on small zoom values or if there are a lot of markers on the map
					const distanceKoef = 1; //markerProps.text !== 'A' ? 1.5 : 1;

					// it's just a simple example, you can tweak distance function as you wish
					return (
						distanceKoef *
						Math.sqrt(
							(x - mousePos.x) * (x - mousePos.x) +
							(y - mousePos.y) * (y - mousePos.y)
						)
					);
				}}
				hoverDistance={K_LISTING_MARKER_SIZE + 0}
				yesIWantToUseGoogleMapApiInternals
				onGoogleApiLoaded={x => handleApiLoaded(x.map, x.maps)}
			>
				{visibleMarkers.map(m => (
						// Banderin Precio
						<ListingMarker
						key={m.id}
						id={m.id}
						lat={m.latitude}
						lng={m.longitude}
						text={m.id}
						active={selectedMarker === Number(m.id)}
						zoomPrecio={zoomPrecio}
						pointType={m.pointType}
						listing={m}
						zoom={zoom}
						updateInfoWindowPosition={updateInfoWindowPosition}
						onClick={handleChildClick}
					/>
				))}
				{isMapFeatured.map(m => {
					return (
						// Banderin Cerca a mi
						<ListingMarker
							key={m.id}
							id={m.id}
							lat={m.latitude}
							lng={m.longitude}
							text={m.id}
							active={selectedMarker === Number(m.id)}
							zoomPrecio={true}
							isMapFeatured={true}
							pointType={m.pointType}
							listing={m}
							zoom={zoom}
							updateInfoWindowPosition={updateInfoWindowPosition}
							onClick={handleChildClick}
						/>
					);
				})}
			</GoogleMapReact>
			<RTBhouse page="listing" data={activeMarkers.map(p => p.id)} />
		</div >
	);
};

export default SearchResultsMap;

